import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import PublicLayout from "@/components/PublicLayout";
import {
  ArrowRight, Building2, Check, X, ShieldCheck, LineChart, Radio, Users,
  Award, Cog, KeyRound, Sparkles,
} from "lucide-react";

/**
 * Fase 14.6 · Landing B2B focus (instituciones financieras).
 */
const CASE_TYPES = [
  { name: "Bancos comerciales", desc: "Certificación regulatoria + refresh continuo para equipos comerciales, riesgo y ops." },
  { name: "Sofipos", desc: "Onboarding masivo de asesores + compliance CNBV." },
  { name: "Casas de bolsa", desc: "Certificación bursátil + wealth digital + productos derivados." },
  { name: "Aseguradoras", desc: "Actuarial, fintech y gestión de portafolios de reservas técnicas." },
  { name: "Fintechs", desc: "ML aplicado, riesgo crediticio digital y cumplimiento anti-fraude." },
];

const INCLUYE = [
  { icon: Users, title: "Licencias por asientos", desc: "De 25 a 10,000+ usuarios · roles jerárquicos · SSO SAML opcional." },
  { icon: KeyRound, title: "Onboarding masivo", desc: "Alta por CSV (con validación) o API. Notificaciones y bienvenida automatizadas." },
  { icon: LineChart, title: "Reportería ejecutiva", desc: "MRR de conocimiento, cohort analysis, LTV, funnel y NPS por cohort." },
  { icon: Radio, title: "Salas en vivo", desc: "Jitsi ilimitado con instructores senior + grabación + Q&A + evaluaciones." },
  { icon: Award, title: "Certificados verificables", desc: "Portal público de validación por folio + aval IADA en programas selectos." },
  { icon: Cog, title: "Content Studio con IA", desc: "Crea programas propios que se suman a tu catálogo institucional." },
];

const ANTES_DESPUES = [
  { antes: "Contenido dispersado en PDFs, YouTube privado y Coursera corporate.", con: "Un solo catálogo curado con 195+ programas actualizados." },
  { antes: "Instructor externo a la carta con logística ad hoc.", con: "Instructores senior + salas Jitsi ilimitadas + calendario integrado." },
  { antes: "Reportes manuales en Excel para RRHH y compliance.", con: "Dashboards ejecutivos con MRR de conocimiento, cohorts y KPIs." },
  { antes: "Certificados PDF sin auditoría posible.", con: "Certificados verificables públicamente por folio (aval IADA opcional)." },
  { antes: "Onboarding manual usuario por usuario.", con: "CSV masivo o API con validación · alta en minutos." },
];

const FAQ = [
  { q: "¿Cuál es el rango de precio típico?", a: "Cotización basada en tu equipo. A partir de $500K MXN/año para 50 asientos con acceso completo, incluyendo instructores y reportería. Volúmenes mayores tienen descuento por asiento." },
  { q: "¿Se integra con nuestro HRIS o LMS actual?", a: "Sí. Tenemos Public API v1 + Webhooks para sincronizar usuarios, progreso, certificados y facturación con tu ERP/CRM/HRIS. SSO SAML disponible." },
  { q: "¿Podemos usar nuestro propio branding?", a: "Absolutamente. Multi-tenant permite branding editable, subdominio opcional y catálogo curado sólo para tu institución." },
  { q: "¿Cómo funcionan los certificados?", a: "Todos los programas emiten certificado verificable con folio auditable en portal público. Los programas selectos incluyen aval IADA/Anáhuac sin costo adicional." },
];

export default function ParaInstituciones() {
  return (
    <PublicLayout>
      <Helmet>
        <title>Para instituciones · DCC</title>
        <meta name="description" content="Capacita a tu equipo con la infraestructura DCC: licencias, onboarding masivo, reportería ejecutiva y certificados verificables. Desde $500K MXN/año." />
      </Helmet>

      {/* Hero */}
      <section className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-primary bg-primary/10 border border-primary/30 rounded-full px-3 py-1">
                <Building2 className="w-3 h-3" /> Para instituciones · B2B
              </span>
              <h1 className="mt-6 text-4xl sm:text-5xl font-extrabold text-slate-900 font-display leading-tight">
                Capacitación financiera de élite <br />
                <span className="text-primary">para tu institución</span>
              </h1>
              <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-2xl">
                Licencias por asientos, onboarding masivo, currícula acreditada y reportería
                ejecutiva. Todo en un stack diseñado para operaciones financieras serias.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/demo?type=enterprise"
                        data-testid="b2b-cta-demo"
                        className="inline-flex items-center gap-2 px-6 py-3.5 rounded-md bg-primary hover:brightness-110 text-white font-bold text-sm shadow-lg shadow-primary/30">
                  Agenda una demo <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/catalogo"
                        className="inline-flex items-center gap-2 px-6 py-3.5 rounded-md border border-slate-300 text-slate-900 hover:bg-slate-50 font-semibold text-sm">
                  Ver catálogo
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5">
              <img src="/marketing/superadmin-subs.png" alt="Reportería ejecutiva DCC"
                    className="rounded-xl border border-slate-200 shadow-lg"
                    onError={(e) => { e.currentTarget.style.display = "none"; }} />
            </div>
          </div>
        </div>
      </section>

      {/* Casos que atendemos */}
      <Section eyebrow="Casos que atendemos" title="Especializados en el ecosistema financiero" bg="slate">
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="b2b-case-types">
          {CASE_TYPES.map((c) => (
            <div key={c.name} className="p-4 bg-white border border-slate-200 rounded-xl">
              <p className="text-sm font-bold text-slate-900">{c.name}</p>
              <p className="mt-2 text-xs text-slate-500 leading-relaxed">{c.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Lo que incluye */}
      <Section eyebrow="Lo que incluye" title="Un paquete integral para tu equipo">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="b2b-features">
          {INCLUYE.map((f) => (
            <div key={f.title} className="p-5 bg-white border border-slate-200 rounded-xl">
              <f.icon className="w-5 h-5 text-primary" />
              <p className="mt-3 text-sm font-bold text-slate-900">{f.title}</p>
              <p className="mt-1.5 text-xs text-slate-500 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Antes vs Con DCC */}
      <Section eyebrow="Antes vs Con DCC" title="Cómo se transforma tu operación" bg="slate">
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="b2b-comparison">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-6 py-4 text-xs uppercase tracking-widest text-slate-500 font-semibold">Antes</th>
                <th className="text-left px-6 py-4 text-xs uppercase tracking-widest text-primary font-semibold">Con DCC</th>
              </tr>
            </thead>
            <tbody>
              {ANTES_DESPUES.map((row, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-6 py-4 text-slate-600 align-top">
                    <X className="w-4 h-4 text-red-400 inline mr-2" />
                    {row.antes}
                  </td>
                  <td className="px-6 py-4 text-slate-900 font-medium align-top">
                    <Check className="w-4 h-4 text-emerald-500 inline mr-2" />
                    {row.con}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Pricing / Cotización */}
      <Section eyebrow="Inversión" title="Cotización basada en tu equipo">
        <div className="max-w-3xl mx-auto p-8 rounded-2xl border-2 border-primary/30 bg-white shadow-lg shadow-primary/5 text-center">
          <p className="text-xs uppercase tracking-widest text-primary font-bold">Desde</p>
          <div className="mt-3 flex items-baseline justify-center gap-2">
            <span className="text-5xl font-extrabold text-slate-900 tabular-nums">$500K</span>
            <span className="text-lg font-semibold text-slate-500">MXN / año</span>
          </div>
          <p className="mt-2 text-sm text-slate-600">Base para 50 asientos con acceso completo</p>
          <p className="mt-5 text-sm text-slate-700 leading-relaxed max-w-xl mx-auto">
            El costo escala por volumen de asientos, y varía según el paquete de instructores en
            vivo, número de tenants (subdominios) y personalización de contenido. Cotización a la medida.
          </p>
          <div className="mt-8">
            <Link to="/demo?type=enterprise"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary hover:brightness-110 text-white font-bold text-sm">
              Solicitar cotización <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </Section>

      {/* FAQ */}
      <Section eyebrow="Preguntas frecuentes" title="Antes de la demo" bg="slate">
        <div className="max-w-3xl mx-auto space-y-3" data-testid="b2b-faq">
          {FAQ.map((f, i) => (
            <details key={i} className="p-5 bg-white border border-slate-200 rounded-xl group">
              <summary className="text-sm font-bold text-slate-900 cursor-pointer flex items-center justify-between">
                {f.q}
                <span className="text-primary group-open:rotate-45 transition-transform text-xl">+</span>
              </summary>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </Section>

      {/* CTA final */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="p-10 md:p-14 rounded-2xl bg-slate-900 text-white text-center">
          <ShieldCheck className="w-10 h-10 mx-auto text-primary" />
          <h2 className="mt-4 text-3xl sm:text-4xl font-bold font-display">
            Diseñemos la propuesta para tu institución
          </h2>
          <p className="mt-3 text-base text-slate-300 max-w-xl mx-auto">
            30 minutos con nuestro equipo comercial · propuesta con casos comparables · NDA disponible.
          </p>
          <div className="mt-6">
            <Link to="/demo?type=enterprise"
                    className="inline-flex items-center gap-2 px-8 py-3.5 rounded-md bg-primary hover:brightness-110 text-white font-bold text-base shadow-lg shadow-primary/40">
              Agenda una demo <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

function Section({ eyebrow, title, children, bg }) {
  const bgClass = bg === "slate" ? "bg-slate-50" : "bg-white";
  return (
    <section className={`${bgClass} border-t border-slate-100`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <p className="text-xs uppercase tracking-widest text-primary font-bold">{eyebrow}</p>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-slate-900 font-display leading-tight">{title}</h2>
        </div>
        {children}
      </div>
    </section>
  );
}
